import React from 'react';
import { useLanguage } from '../utils/useLanguage';
import ActivityCard from '../ui/ActivityCard';
import PullQuote from '../ui/PullQuote';

export default function ContentBlockRenderer({ blocks, activities = [] }) {
  const { language, t, getField } = useLanguage();

  if (!blocks || blocks.length === 0) return null;

  const getActivity = (id) => activities.find(a => a.id === id);

  return (
    <div className="space-y-8">
      {blocks.map((block) => {
        if (!block || !block.type) return null;
        const data = block.data || {};

        switch (block.type) {
          case 'heading':
            return (
              <h2 key={block.id} className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mt-12 mb-2">
                {getField(data, 'text')}
              </h2>
            );

          case 'text':
            return (
              <div
                key={block.id}
                className="rich-text text-gray-700 text-lg leading-relaxed max-w-none"
                dangerouslySetInnerHTML={{ __html: getField(data, 'text') || '' }}
              />
            );

          case 'image':
            return (
              <figure key={block.id} className="my-8">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src={data.url}
                    alt={getField(data, 'caption') || ''}
                    className="w-full h-auto"
                  />
                </div>
                {(getField(data, 'caption') || data.credit) && (
                  <figcaption className="mt-2 text-sm text-gray-400 flex items-center justify-between">
                    <span>{getField(data, 'caption')}</span>
                    {data.credit && <span className="text-gray-300">© {data.credit}</span>}
                  </figcaption>
                )}
              </figure>
            );

          case 'activity': {
            const activity = getActivity(data.activity_id);
            if (!activity) return null;
            return (
              <div key={block.id} className="my-8">
                <ActivityCard activity={activity} />
              </div>
            );
          }

          case 'video': {
            const url = data.url || '';
            const isYouTube = url.includes('youtube.com') || url.includes('youtu.be');
            const youtubeId = isYouTube
              ? url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([^&?\s]+)/)?.[1]
              : null;
            return (
              <figure key={block.id} className="my-8">
                <div className="rounded-2xl overflow-hidden aspect-video bg-gray-100">
                  {isYouTube && youtubeId ? (
                    <iframe
                      src={`https://www.youtube.com/embed/${youtubeId}`}
                      title={getField(data, 'caption') || 'Video'}
                      className="w-full h-full"
                      allowFullScreen
                    />
                  ) : url ? (
                    <video src={url} controls className="w-full h-full" />
                  ) : null}
                </div>
                {getField(data, 'caption') && (
                  <figcaption className="mt-2 text-sm text-gray-400">{getField(data, 'caption')}</figcaption>
                )}
              </figure>
            );
          }

          case 'gallery':
            return (
              <div key={block.id} className="my-8">
                {data.images && data.images.length > 0 && (
                  <div className={`grid gap-3 ${data.images.length === 2 ? 'grid-cols-2' : data.images.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                    {data.images.map((img, i) => (
                      <figure key={i} className="rounded-xl overflow-hidden">
                        <img src={img.url} alt={getField(img, 'caption') || ''} className="w-full h-auto" />
                        {getField(img, 'caption') && (
                          <figcaption className="mt-1 text-xs text-gray-400">{getField(img, 'caption')}</figcaption>
                        )}
                      </figure>
                    ))}
                  </div>
                )}
              </div>
            );

          case 'quote':
            return (
              <PullQuote key={block.id} author={data.author ? `— ${data.author}` : null} className="my-10">
                {getField(data, 'text')}
              </PullQuote>
            );

          default:
            return null;
        }
      })}
    </div>
  );
}